import React from 'react'
import { AppState } from '../../../../stores/app'
import { AppService } from '../../../../services'
import { FormInstance } from 'antd/lib/form'
import { Container } from 'typescript-ioc'

import {
    ALL_SCHEDULE_DAYS,
    EveryNSchedule,
    EveryNScheduleType,
    IEveryNScheduleValue,
    DayPartSelect,
    IDayPartValue,
} from '@pushly/aqe/lib/components'
import type {
    IRecurrenceSchedule,
    ScheduleType,
    IScheduleRelativeDay,
} from '@pushly/models/lib/interfaces/recurrence-schedule'
import { SavedReportScheduleModel } from '../../../../models/saved-reports/saved-report-schedule.model'
import { SRScheduleContext } from '../saved-report-schedule-context'
import { RadioChangeEvent } from 'antd/lib/radio'
import { numRange } from '../../../../_utils/range'
import { getClassNames } from '../../../../_utils/classnames'
import { WarningOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { BetterTimePicker } from '@pushly/aqe/lib/components/better-timepicker/better-timepicker'
import { Flex, Form, Radio, Select, Space, Tooltip } from 'antd'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@pushly/aqe'

interface IScheduleBuilderProps {
    schedule?: SavedReportScheduleModel
}

interface IScheduleBuilderState {
    endDateEnabled: boolean
}

export class SavedReportScheduleBuilder extends React.Component<IScheduleBuilderProps, IScheduleBuilderState> {
    public static contextType = SRScheduleContext
    public context!: React.ContextType<typeof SRScheduleContext>
    protected readonly appState: AppState
    protected readonly appService: AppService

    protected formRef = React.createRef<FormInstance>()

    public constructor(props: IScheduleBuilderProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public render() {
        const savedReportSchedule = this.props.schedule ?? this.context.savedReportSchedule
        const config = savedReportSchedule.getConfiguration()

        const schedule = config.getSchedule()!
        const scheduleType = schedule?.type
        const scheduleSuperType = scheduleType?.replace(/_relative|_specific/, '')

        const timezone = this.appState?.currentDomain?.timezone ?? 'UTC' // forced UTC for working with date strings only
        schedule.time_zone = timezone
        const today = dayjs().startOf('day').add(7, 'hours')

        const defaultTime = (schedule.specific_time && dayjs(schedule.specific_time, 'HH:mm')) || today
        const timeValue = schedule.specific_time !== undefined ? dayjs(schedule.specific_time, 'HH:mm') : defaultTime

        return (
            <Flex vertical className="schedule-well">
                <Form.Item label="Schedule Type">
                    <Space>
                        <Select
                            size="small"
                            value={scheduleSuperType}
                            onChange={this.handleScheduleTypeChange}
                            dropdownClassName="schedule-type-dropdown"
                            style={{ width: 120 }}
                        >
                            <Select.Option value="daily">Daily</Select.Option>
                            <Select.Option value="weekly">Weekly</Select.Option>
                            <Select.Option value="monthly">Monthly</Select.Option>
                        </Select>
                        {/^monthly/.test(scheduleType) && (
                            <Radio.Group
                                size="small"
                                className="journey-trigger-editor-rec-schedule-monthly-type-select"
                                value={schedule.relative_type ?? scheduleType}
                                onChange={this.handleMonthlyTypeChange}
                            >
                                <Tooltip title="The schedule will deliver once a month on the chosen day number of the month.">
                                    <Radio.Button value="monthly_specific">Specific Day</Radio.Button>
                                </Tooltip>
                                <Tooltip title="The schedule will deliver once a month on the first day of the month.">
                                    <Radio.Button value="first_day">First Day</Radio.Button>
                                </Tooltip>
                                <Tooltip title="The schedule will deliver once a month on the last day of the month.">
                                    <Radio.Button value="last_day">Last Day</Radio.Button>
                                </Tooltip>
                                <Tooltip title="The schedule will deliver once a month on the chosen relative date of the month.">
                                    <Radio.Button value="relative_day">Relative Day</Radio.Button>
                                </Tooltip>
                            </Radio.Group>
                        )}
                    </Space>
                </Form.Item>

                {scheduleSuperType !== 'monthly' && (
                    <Form.Item label={`${scheduleType === 'daily' ? 'Daily' : 'Weekly'} Schedule`}>
                        <EveryNSchedule
                            key={scheduleType}
                            type={scheduleType as EveryNScheduleType}
                            defaultValue={
                                this.context.mode > 0
                                    ? {
                                          type: schedule.type as EveryNScheduleType,
                                          frequency: schedule.frequency ?? 1,
                                          allowed_days: schedule.allowed_days ?? [],
                                      }
                                    : undefined
                            }
                            value={{
                                type: schedule.type as EveryNScheduleType,
                                frequency: schedule.frequency ?? 1,
                                allowed_days: schedule.allowed_days ?? [],
                            }}
                            onChange={this.handleMonthlyEveryNChange}
                        />
                    </Form.Item>
                )}

                {scheduleType === 'monthly_specific' && (
                    <Form.Item label="Specific Day">
                        <Select
                            className="specific-day-select"
                            size="small"
                            defaultValue={1}
                            value={schedule.specific_day ?? 1}
                            onChange={this.handleMonthlySpecificDayChange}
                            style={{ width: 120 }}
                        >
                            {numRange(1, 31).map((day) => (
                                <Select.Option key={day} value={day}>
                                    {day}
                                </Select.Option>
                            ))}
                        </Select>
                        {(schedule.specific_day ?? 1) > 28 && (
                            <div className={getClassNames('form-layout-row', 'specific-day-warning')}>
                                <div className={getClassNames('form-layout-row-sub')}>
                                    <WarningOutlined />
                                    <span>
                                        This scheduled delivery will not deliver in months that do not have the chosen
                                        number of days. To always target the last day of the month use the “Relative
                                        Day” option.
                                    </span>
                                </div>
                            </div>
                        )}
                    </Form.Item>
                )}

                {schedule.relative_type === 'relative_day' && (
                    <Form.Item label="Relative Day">
                        <Space>
                            <Select
                                className="relative-day-ordinal-select"
                                dropdownClassName="relative-day-dropdown"
                                size="small"
                                value={schedule.relative_day?.ordinal ?? 'first'}
                                onChange={this.handleRelativeDayOrdinalChange}
                                style={{ width: 120 }}
                                options={[
                                    { value: 'first', label: 'First' },
                                    { value: 'second', label: 'Second' },
                                    { value: 'third', label: 'Third' },
                                    { value: 'fourth', label: 'Fourth' },
                                    { value: 'fifth', label: 'Fifth' },
                                ]}
                            />

                            <DayPartSelect
                                className="relative-day-select"
                                size="small"
                                mode="single"
                                daysOnly={true}
                                dayFormat={'ddd'}
                                weekBeginsOnSunday={true}
                                defaultValue={this.buildAllowedDayMap([this.getRelativeDayString(schedule)])}
                                value={this.buildAllowedDayMap([this.getRelativeDayString(schedule)])}
                                onChange={this.handleRelativeDayValueChange}
                            />
                        </Space>
                    </Form.Item>
                )}

                <Form.Item label="Delivery Time">
                    <Space.Compact>
                        <BetterTimePicker
                            size="small"
                            placeholder="Start time"
                            use12Hours={true}
                            format="h A"
                            defaultValue={defaultTime}
                            value={timeValue}
                            onTimeChange={this.handleScheduleTimeChange}
                            showNow={false}
                        />
                        <Space.Addon>{timezone}</Space.Addon>
                    </Space.Compact>
                </Form.Item>
            </Flex>
        )
    }

    protected buildAllowedDayMap(days: string[]): IDayPartValue {
        const activeDays = new Set(days.filter((d) => !!d))

        return {
            monday: activeDays.has('monday'),
            tuesday: activeDays.has('tuesday'),
            wednesday: activeDays.has('wednesday'),
            thursday: activeDays.has('thursday'),
            friday: activeDays.has('friday'),
            saturday: activeDays.has('saturday'),
            sunday: activeDays.has('sunday'),
        }
    }

    protected getRelativeDayString(schedule: IRecurrenceSchedule): string {
        const rd = schedule.relative_day
        if (!rd) return ''
        return rd.day ?? ''
    }

    protected handleScheduleTypeChange = (type: string) => {
        const savedReportSchedule = this.context.savedReportSchedule.clone()
        const config = savedReportSchedule.getConfiguration()
        const schedule = config.getSchedule()!
        const scheduleType = schedule?.type
        const scheduleSuperType = scheduleType?.replace(/_relative|_specific/, '')

        if (type !== 'monthly') {
            if (scheduleSuperType === 'monthly') {
                schedule.frequency = 1
            }
            schedule.allowed_days = type === 'daily' ? ALL_SCHEDULE_DAYS : ['monday']
        } else {
            // Set default monthly type
            type = 'monthly_relative'
            schedule.relative_day = { ordinal: 'first', day: 'monday' } as IScheduleRelativeDay
            schedule.relative_type = 'relative_day'
        }

        schedule.type = type as ScheduleType

        this.context.setSavedReportSchedule(savedReportSchedule)
    }

    protected handleMonthlyTypeChange = (ev: RadioChangeEvent) => {
        const savedReportSchedule = this.context.savedReportSchedule.clone()
        const config = savedReportSchedule.getConfiguration()
        const schedule = config.getSchedule()!

        let type = ev.target.value
        if (type === 'monthly_specific') {
            schedule.specific_day = 1
            schedule.relative_type = undefined
        } else {
            schedule.relative_type = type
            if (type === 'relative_day') {
                schedule.relative_day = { ordinal: 'first', day: schedule.relative_day?.day ?? 'monday' }
            }

            type = 'monthly_relative'
        }

        schedule.type = type
        this.context.setSavedReportSchedule(savedReportSchedule)
    }

    protected handleMonthlyEveryNChange = (value: IEveryNScheduleValue | undefined) => {
        const savedReportSchedule = this.context.savedReportSchedule.clone()
        const config = savedReportSchedule.getConfiguration()
        const schedule = config.getSchedule()!

        schedule.frequency = value?.frequency
        schedule.allowed_days = value?.allowed_days

        this.context.setSavedReportSchedule(savedReportSchedule)
    }

    protected handleMonthlySpecificDayChange = (day: number) => {
        const savedReportSchedule = this.context.savedReportSchedule.clone()
        const config = savedReportSchedule.getConfiguration()
        const schedule = config.getSchedule()!

        schedule.specific_day = day

        this.context.setSavedReportSchedule(savedReportSchedule)
    }

    protected handleRelativeDayOrdinalChange = (ordinal: string) => {
        const savedReportSchedule = this.context.savedReportSchedule.clone()
        const config = savedReportSchedule.getConfiguration()
        const schedule = config.getSchedule()!

        schedule.relative_day = {
            ordinal,
            day: schedule.relative_day?.day ?? 'monday',
        }

        this.context.setSavedReportSchedule(savedReportSchedule)
    }

    protected handleRelativeDayValueChange = async (days: IDayPartValue) => {
        const savedReportSchedule = this.context.savedReportSchedule.clone()
        const config = savedReportSchedule.getConfiguration()
        const schedule = config.getSchedule()!

        const dayKeys = Object.keys(days).filter((d) => !!days[d])
        schedule.relative_day = {
            ordinal: schedule.relative_day?.ordinal ?? 'first',
            day: dayKeys.length > 0 ? dayKeys[0] : 'monday',
        }

        this.context.setSavedReportSchedule(savedReportSchedule)
    }

    protected handleScheduleTimeChange = (dtpValue: Dayjs | null) => {
        const savedReportSchedule = this.context.savedReportSchedule.clone()
        const config = savedReportSchedule.getConfiguration()
        const schedule = config.getSchedule()!

        schedule.specific_time = dtpValue?.format('HH:mm')

        this.context.setSavedReportSchedule(savedReportSchedule)
    }

    protected handleStartDateChange = (date: Dayjs) => {
        const savedReportSchedule = this.context.savedReportSchedule.clone()

        const startDate = !date ? undefined : date.format('YYYY-MM-DD')
        savedReportSchedule.setDateStart(startDate)

        this.context.setSavedReportSchedule(savedReportSchedule)
    }

    protected handleEndDateChange = (date: Dayjs) => {
        const savedReportSchedule = this.context.savedReportSchedule.clone()

        const endDate = !date ? undefined : date.format('YYYY-MM-DD')
        savedReportSchedule.setDateEnd(endDate)

        this.context.setSavedReportSchedule(savedReportSchedule)
    }

    protected handleEndDateToggle = (endDateEnabled: boolean) => {
        const savedReportSchedule = this.context.savedReportSchedule.clone()

        if (!endDateEnabled) {
            savedReportSchedule.setDateEnd(undefined)
        }

        this.setState({ endDateEnabled: !this.state.endDateEnabled })
    }
}
